import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../components/ui/pagination';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Loader2, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const REVIEWS_PER_PAGE = 3;

export default function ReviewSection({ propertyId, ownerId, onReviewAdded }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    rating: 5,
    comment: '',
  });

  const [replyData, setReplyData] = useState({});

  useEffect(() => {
    let cancelled = false;

    // Demo listings have no backend reviews — skip the network call
    // so the page never hangs on a doomed request.
    const isDemo =
      (typeof propertyId === 'string' && propertyId.startsWith('demo-')) ||
      localStorage.getItem('token') === 'demo-session-token';

    if (isDemo) {
      setReviews([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const fetchReviews = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${API_URL}/api/reviews/property/${propertyId}`, {
          headers,
          signal: controller.signal,
        });

        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setReviews(data.reviews || []);
        }
      } catch (_error) {
        // Silently fall back to empty reviews — network hiccups shouldn't
        // break the property page.
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [propertyId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please log in to submit a review');
      navigate('/login', { state: { from: `/property/${propertyId}` } });
      return;
    }

    if (!formData.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          property_id: propertyId,
          rating: formData.rating,
          comment: formData.comment,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }

      const newReview = await response.json();
      setReviews([newReview, ...reviews]);
      setFormData({ rating: 5, comment: '' });
      toast.success('Review submitted successfully!');

      if (onReviewAdded) {
        onReviewAdded(newReview);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyChange = (reviewId, value) => {
    setReplyData(prev => ({
      ...prev,
      [reviewId]: value,
    }));
  };

  const handleSubmitReply = async (reviewId) => {
    if (!replyData[reviewId]?.trim()) {
      toast.error('Please write a reply');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}/reply`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          reply: replyData[reviewId],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit reply');
      }

      setReviews(reviews.map(rev =>
        rev.id === reviewId
          ? { ...rev, owner_reply: replyData[reviewId] }
          : rev
      ));

      setReplyData(prev => ({
        ...prev,
        [reviewId]: '',
      }));

      toast.success('Reply submitted!');
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error(error.message);
    }
  };

  const paginatedReviews = reviews.slice(
    (currentPage - 1) * REVIEWS_PER_PAGE,
    currentPage * REVIEWS_PER_PAGE
  );
  const totalPages = Math.ceil(reviews.length / REVIEWS_PER_PAGE);

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const renderStars = (rating, onClick = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <button
            key={i}
            type="button"
            onClick={() => onClick && onClick(i)}
            className={`transition-colors ${onClick ? 'cursor-pointer hover:opacity-70' : ''}`}
          >
            <Star
              className={`h-4 w-4 ${
                i <= rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="h-8 bg-slate-200 rounded animate-pulse" />
            <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-slate-900">Reviews ({reviews.length})</h3>
            <div className="text-right">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {averageRating}
                </div>
                {renderStars(Math.round(averageRating))}
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          {reviews.length > 0 && (
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(rating => {
                const count = reviews.filter(r => r.rating === rating).length;
                const percent = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center gap-2 text-xs">
                    <span className="w-8 text-slate-600">{rating}★</span>
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="w-6 text-right text-slate-600">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Write Review Form */}
      {isAuthenticated && user?.id !== ownerId && !reviews.find(r => r.reviewer_id === user.id) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Write a Review</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div className="space-y-2">
                  <Label>Rating *</Label>
                  <div className="flex gap-2">
                    {renderStars(formData.rating, (rating) =>
                      setFormData(prev => ({ ...prev, rating }))
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comment">Your Review *</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Share your experience..."
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-accent hover:bg-accent/90"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Review
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Reviews List */}
      {paginatedReviews.length > 0 ? (
        <div className="space-y-4">
          <AnimatePresence>
            {paginatedReviews.map((review, idx) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card>
                  <CardContent className="p-6 space-y-4">
                    {/* Reviewer Info */}
                    <div className="flex items-start justify-between">
                      <div className="flex gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={review.reviewer_picture} alt={review.reviewer_name} />
                          <AvatarFallback>
                            {review.reviewer_name?.split(' ')[0]?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{review.reviewer_name}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(review.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      {renderStars(review.rating)}
                    </div>

                    {/* Review Comment */}
                    <p className="text-slate-700 text-sm">{review.comment}</p>

                    {/* Owner Reply */}
                    {review.owner_reply && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Owner's Reply</p>
                        <p className="text-slate-700 text-sm">{review.owner_reply}</p>
                      </div>
                    )}

                    {/* Reply Form for Owner */}
                    {isAuthenticated && user?.id === ownerId && !review.owner_reply && (
                      <div className="border-t border-slate-200 pt-4">
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-600">Reply</label>
                          <div className="flex gap-2">
                            <Textarea
                              value={replyData[review.id] || ''}
                              onChange={(e) => handleReplyChange(review.id, e.target.value)}
                              placeholder="Share your response..."
                              rows={2}
                              className="text-sm flex-1"
                            />
                            <Button
                              type="button"
                              onClick={() => handleSubmitReply(review.id)}
                              size="sm"
                              className="h-fit"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-slate-600">No reviews yet. Be the first to review!</p>
            </CardContent>
          </Card>
        )
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationPrevious
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            />
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = currentPage - 2 + i;
              return page > 0 && page <= totalPages ? (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ) : null;
            })}
            <PaginationNext
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
}
